import { Component, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { User } from '@auth0/auth0-spa-js/';
import { filter } from 'rxjs/operators';
import { AppService } from './core/services/app.service';
import { environment } from 'src/environments/environment';
import { Tenant } from './core/model/tenants/tenant.model';
import { ClientDashboardMetadata, ClientLayout, ClientLayoutItem, ClientLayoutItemType } from '@mmlogic/components';
import { ApiClientService } from './core/services/api-client.service';
import { ModelStatusService } from './core/services/model-status.service';
import { getDefaultLogo } from './core/utils/default-logo.util';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  user: User | null | undefined;
  isAuthenticated = false;
  tenantName: string;
  tenantCode: string;
  logo = getDefaultLogo();
  private isHomePage = false;
  private isSignupPage = false;
  generalSearchLayoutItem: ClientLayoutItem | null = null;

  navigation: ClientDashboardMetadata | null = null;
  navLayouts: ClientLayout[] = [];

  constructor(private appService: AppService,
    private api: ApiClientService,
    private auth: AuthService,
    private router: Router,
    private titleService: Title,
    private modelStatus: ModelStatusService,
    private destroyRef: DestroyRef) { }

  ngOnInit(): void {
    this.appService.setPath(this.router.url);
    this.isHomePage = this.router.url === '/';
    this.isSignupPage = this.router.url.startsWith('/signup/');

    this.auth.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => this.user = user);
    this.auth.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(auth => this.isAuthenticated = auth);
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.appService.onNavigate(event);
    });
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((e: NavigationEnd) => {
      this.isHomePage = e.urlAfterRedirects === '/';
      this.isSignupPage = e.urlAfterRedirects.startsWith('/signup/');
    });
    this.appService.getCurrentTenant().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(tenant => this.selectTenant(tenant));
    this.modelStatus.watch().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  selectTenant(tenant: Tenant | null) {
    this.navigation = null;
    this.navLayouts = [];
    this.generalSearchLayoutItem = null;

    if (tenant) {
      this.tenantName = tenant.name;
      this.tenantCode = tenant.tenantCode;
      this.logo = tenant.logo ? environment.apiUrl + tenant.logo : getDefaultLogo();
      this.titleService.setTitle(`Evidend: ${tenant.name}`);

      if (tenant.generalSearchClasses?.length) {
        this.generalSearchLayoutItem = {
          type: ClientLayoutItemType.SEARCH,
          label: tenant.name,
          search: { dataClass: tenant.generalSearchClasses.join(','), showTitle: false },
        };
      }

      this.api.getNavigationMetadata(tenant.tenantCode).subscribe(dashboard => {
        this.navigation = dashboard;
        this.navLayouts = dashboard.layouts;
      });
    } else {
      this.titleService.setTitle('Evidend');
    }
  }

  onHome() {
    if (this.appService.hasCurrentTenant()) {
      this.router.navigate([this.tenantCode]);
    } else {
      this.router.navigate(['/']);
    }
  }

  onSelectTenant() {
    this.router.navigate(['/'], { queryParams: { choose: 'true' } });
  }

  get isLoginPage(): boolean {
    return (this.isHomePage && !this.isAuthenticated) || this.isSignupPage;
  }

  get locale(): string {
    return this.appService.getLocale();
  }

  layoutHasIcons(layout: ClientLayout): boolean {
    return layout.items.some(item => !!item.icon);
  }

  getNavIcon(layoutItem: ClientLayoutItem): string | null {
    const icon = layoutItem.icon ?? null;
    return icon ? resolveIconHref(icon) : null;
  }

  onNavigate(layoutItem: ClientLayoutItem): void {
    if (!this.tenantCode) return;
    if (layoutItem.navigationKind === 'OBJECT_FORM_DASHBOARD') {
      this.router.navigate(['data', this.tenantCode, layoutItem.dataClass, 'new']);
    } else if (layoutItem.navigationKind === 'DASHBOARD') {
      this.router.navigate([this.tenantCode], { queryParams: layoutItem.name ? { name: layoutItem.name } : {} });
    } else if (layoutItem.navigationKind === 'SYSTEM_DASHBOARD') {
      this.router.navigate(['system', this.tenantCode, layoutItem.dataClass]);
    } else {
      this.router.navigate([this.tenantCode, layoutItem.dataClass]);
    }
  }
}

function resolveIconHref(icon: string): string {
  if (icon.startsWith('/') || icon.startsWith('http')) return icon;
  const hashIndex = icon.indexOf('#');
  if (hashIndex > 0) {
    return `/assets/${icon.substring(0, hashIndex)}.svg${icon.substring(hashIndex)}`;
  }
  return icon;
}
